'use client'

import Link from 'next/link'
import { cx } from '@/src/styles/stylebook'
import Multilingual from '@/src/components/global/Multilingual'
import { listBallPathD, ListBall } from '@/src/components/common/frame/Ball'
import { urlForImage } from '@/sanity/lib/utils'
import { motion } from 'framer-motion'
import { useEffect, useId, useState } from 'react'
import useScreenSize from '@/src/hooks/useScreenSize'

type SectionItem = {
  _id: string
  slug: string | null
  title?: string | { [lang: string]: string } | null
  thumbnail?: any
}

type Props = {
  data: SectionItem[]
}

export default function SectionsList({ data }: Props) {
  return (
    <div className="pt-gutter flex w-full flex-wrap max-xl:pb-50 max-md:pt-[calc(var(--headerh)+var(--spacing-gutter))]">
      {data.map((section, i) => (
        <SectionsListItem key={section._id} section={section} index={i} />
      ))}
    </div>
  )
}

function SectionsListItem({ section, index }: { section: SectionItem; index: number }) {
  const [isTile, setIsTile] = useState(false)
  const { width } = useScreenSize()
  const [isMobile, setIsMobile] = useState(false)
  const clipId = useId()
  const imgUrl = urlForImage(section.thumbnail)?.width(1200).height(690).fit('crop').url()

  useEffect(() => {
    if (width <= 640) {
      setIsTile(true)
      setIsMobile(true)
    } else if (width <= 1280) {
      setIsTile(index % 2 === 1)
      setIsMobile(false)
    } else {
      setIsTile(index % 3 === 1)
      setIsMobile(false)
    }
  }, [width])

  return (
    <Link
      href={section.slug ? `/programs/sections/${section.slug}` : '#'}
      className={cx(
        'group bold h5 text-gray-40 relative flex aspect-[509/292] w-1/3 items-center justify-center rounded-full hover:z-10 hover:text-green-50',
        'max-xl:w-1/2 max-md:-mt-5 max-md:w-full max-md:first:mt-0'
      )}
    >
      <motion.div
        className="relative flex h-full w-full items-center justify-center"
        initial="rest"
        animate="rest"
        whileHover="hover"
      >
        {/* Rotating overlay wrapper (image + rings) */}
        <motion.div
          className="pointer-events-none absolute top-0 right-0 bottom-0 left-0 z-0 flex h-full w-full transform-gpu items-center justify-center"
          variants={{ rest: { rotate: 0 }, hover: { rotate: -4 } }}
          transition={{ type: 'spring', stiffness: 300, damping: 24 }}
        >
          {/* Base SVG fill */}
          <ListBall
            className={cx(
              'absolute top-0 right-0 bottom-0 left-0 z-0 h-full w-full',
              isTile ? 'fill-system-tilebg' : 'fill-gray-100'
            )}
            preserveAspectRatio="none"
          />
          {/* Thumbnail clipped to ListBall shape */}
          {imgUrl && (
            <motion.svg
              className="absolute top-0 right-0 bottom-0 left-0 h-full w-full max-md:opacity-100!"
              viewBox="0 0 509 292"
              preserveAspectRatio="none"
              variants={{ rest: { opacity: 0 }, hover: { opacity: 1 } }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              <defs>
                <clipPath id={`${clipId}-img`}>
                  <path d={listBallPathD} />
                </clipPath>
              </defs>
              <g clipPath={`url(#${clipId}-img)`}>
                <image
                  href={imgUrl}
                  x={0}
                  y={0}
                  width={509}
                  height={292}
                  preserveAspectRatio="xMidYMid slice"
                />
                <rect x={0} y={0} width={509} height={292} className="fill-black" opacity={0.6} />
              </g>
            </motion.svg>
          )}

          {/* Hover inner rings overlay using clipPath */}
          <motion.svg
            className="absolute top-0 right-0 bottom-0 left-0 h-full w-full max-md:opacity-100!"
            viewBox="0 0 509 292"
            preserveAspectRatio="none"
            variants={{ rest: { opacity: 0 }, hover: { opacity: 1 } }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <defs>
              <clipPath id={clipId}>
                <path d={listBallPathD} />
              </clipPath>
            </defs>
            <g clipPath={`url(#${clipId})`}>
              {/* green inner ring (6~12px) */}
              <path
                d={listBallPathD}
                fill="none"
                stroke="currentColor"
                strokeWidth={isMobile ? 16 : 24}
                vectorEffect="non-scaling-stroke"
                className="text-green-50"
              />
              {/* black inner ring (0~6px) */}
              <path
                d={listBallPathD}
                fill="none"
                stroke="currentColor"
                strokeWidth={isMobile ? 8 : 12}
                vectorEffect="non-scaling-stroke"
                className="text-gray-0"
              />
            </g>
          </motion.svg>
        </motion.div>

        <span className="max-md:h3 pointer-events-none z-1 max-md:text-green-50">
          <Multilingual>{section.title as any}</Multilingual>
        </span>
      </motion.div>
    </Link>
  )
}
